/**
 * Read access to the Cabinet catalog (Cabinet v1).
 *
 * Powers the `/cabinet` graph view: the deal/document/entity graph plus the
 * corpus-level summary strip. All network access goes through `apiFetch` (never
 * raw `fetch`); responses are validated at the boundary and a bad payload
 * degrades to `null` so the page can show an error state rather than render
 * against an unknown shape.
 */

import {
	cabinetGraphFixture,
	cabinetSummaryFixture,
} from "../fixtures/cabinetGraph.fixture";
import {
	type CabinetGraph,
	type CabinetSummary,
	parseCabinetGraph,
	parseCabinetSummary,
} from "../types/cabinet";
import { apiFetch } from "../utils/apiClient";

/**
 * Dev-only fixture mode: `http://localhost:3000/cabinet?fixture=1` renders the
 * canonical fixture without touching the API, so the view can be built and
 * reviewed before the backend router lands.
 *
 * Two guards keep this out of production: `NODE_ENV === "development"` (CRA
 * hard-codes `"production"` in `npm run build`, so the branch is dead code that
 * gets stripped) and the explicit opt-in query param. It short-circuits BEFORE
 * the request rather than acting as a fallback, so it can never mask a live API
 * error.
 */
function shouldUseFixture(): boolean {
	if (process.env.NODE_ENV !== "development") return false;
	if (typeof window === "undefined") return false;
	return new URLSearchParams(window.location.search).get("fixture") === "1";
}

class CabinetService {
	/** Fetch the full cabinet graph. Returns `null` on any failure. */
	async getCabinetGraph(): Promise<CabinetGraph | null> {
		if (shouldUseFixture()) return cabinetGraphFixture;
		try {
			const resp = await apiFetch("/api/v3/cabinet/graph");
			if (!resp.ok) return null;
			return parseCabinetGraph(await resp.json());
		} catch {
			return null;
		}
	}

	/** Fetch corpus-level counts. Returns `null` on any failure. */
	async getCabinetSummary(): Promise<CabinetSummary | null> {
		if (shouldUseFixture()) return cabinetSummaryFixture;
		try {
			const resp = await apiFetch("/api/v3/cabinet/summary");
			if (!resp.ok) return null;
			return parseCabinetSummary(await resp.json());
		} catch {
			return null;
		}
	}
}

export const cabinetService = new CabinetService();
export default cabinetService;
